import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { InboxConversationSummary } from '@unipostudio/shared-types';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import { PlatformIcon, type PlatformId } from '@/shared/components/PlatformIcon';
import { useToast } from '@/shared/components/Toast';
import { inboxService, type InboxEvent } from '@/shared/services/inbox.service';
import { useInboxBadges } from '@/shared/hooks/useInboxBadges';
import { useInboxMessages, useReplyToMessage } from '@/shared/hooks/useInbox';
import { useCanWrite } from '@/shared/hooks/useCanWrite';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { canReadInbox } from '@/shared/lib/role-access';
import { formatRelative, formatTime } from '@/shared/lib/intl';

/** Pages where the dock would sit on top of the same thing, or on a focused flow. */
const HIDDEN_ON = [ROUTES.INBOX, ROUTES.COMPOSER, ROUTES.ONBOARDING, ROUTES.ADMIN, ROUTES.BRAND_SETUP];
const DOCK_LIMIT = 8;

type Summary = InboxConversationSummary<InboxEvent>;

function lastInbound(c: Summary): InboxEvent | undefined {
  return c.events.find((e) => e.direction === 'INBOUND') ?? c.events[0];
}

function nameOf(c: Summary): string {
  const e = lastInbound(c);
  return e?.fromName || e?.fromHandle || '—';
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function Avatar({ c, size = 32 }: { c: Summary; size?: number }) {
  const e = lastInbound(c);
  const name = nameOf(c);
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      {e?.fromAvatarUrl ? (
        <img src={e.fromAvatarUrl} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} referrerPolicy="no-referrer" />
      ) : (
        <span className="rounded-full flex items-center justify-center text-ui font-semibold bg-[var(--color-surface-3)] text-[var(--ink-2)]" style={{ width: size, height: size }} aria-hidden="true">
          {initialOf(name)}
        </span>
      )}
      <span className="absolute -bottom-0.5 -end-0.5 rounded-full bg-[var(--color-surface)] p-[1px]">
        <PlatformIcon platform={c.channel.toLowerCase() as PlatformId} size={12} decorative />
      </span>
    </span>
  );
}

/** One bubble per message: the customer on the start side, the team on the end side. */
function threadLines(events: InboxEvent[]): Array<{ id: string; text: string; at: string; mine: boolean }> {
  const lines: Array<{ id: string; text: string; at: string; mine: boolean }> = [];
  for (const e of events) {
    if (e.message) lines.push({ id: e.id, text: e.message, at: e.createdAt, mine: e.direction === 'OUTBOUND' });
    for (const r of e.replies ?? []) lines.push({ id: `${e.id}:${r.id}`, text: r.text, at: r.sentAt, mine: true });
  }
  return lines.sort((a, b) => a.at.localeCompare(b.at));
}

function ChatWindow({ c, onClose, onOpenInbox }: { c: Summary; onClose: () => void; onOpenInbox: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { canWrite } = useCanWrite();
  const [text, setText] = useState('');
  const thread = useInboxMessages({ conversation: c.key, limit: 30, isArchived: false }, { poll: true });
  const target = lastInbound(c);
  const replyWindow = useQuery({
    queryKey: ['inbox', 'reply-window', target?.id],
    queryFn: () => inboxService.replyWindow(target?.id ?? ''),
    enabled: !!target?.id,
    staleTime: 60_000,
  });
  const reply = useReplyToMessage();
  const canSend = canWrite && (replyWindow.data ? replyWindow.data.canReply && (replyWindow.data.replyWindow?.canSendFreeform ?? true) : true);
  const lines = useMemo(() => threadLines(thread.data?.data ?? []), [thread.data]);

  const send = (): void => {
    const message = text.trim();
    if (!message || !target) return;
    reply.mutate(
      { eventId: target.id, message, idempotencyKey: crypto.randomUUID() },
      {
        onSuccess: () => setText(''),
        onError: () => toast('error', t('dock.sendFailed'), undefined, { label: t('dock.openInbox'), onClick: onOpenInbox }),
      },
    );
  };

  return (
    <section className="pointer-events-auto w-[330px] h-[440px] flex flex-col bg-[var(--color-surface)] border border-b-0 border-[var(--line)] rounded-t-[12px] shadow-[var(--shadow-xl)]" aria-label={t('dock.chatWith', { name: nameOf(c) })}>
      <header className="h-12 flex items-center gap-2 ps-3 pe-1.5 border-b border-[var(--line)] shrink-0">
        <Avatar c={c} size={28} />
        <div className="flex-1 min-w-0">
          <div className="text-ui font-semibold truncate" dir="auto">{nameOf(c)}</div>
        </div>
        <button type="button" className="btn btn-ghost btn-icon-sm" onClick={onOpenInbox} aria-label={t('dock.openInbox')} title={t('dock.openInbox')}>
          <Icons.externalLink size={16} aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-ghost btn-icon-sm" onClick={onClose} aria-label={t('dock.close')}>
          <Icons.close size={16} aria-hidden="true" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto overscroll-contain p-3 flex flex-col gap-2" aria-live="polite">
        {thread.isLoading && <span className="sr-only" role="status">{t('dock.loading')}</span>}
        {thread.isLoading && Array.from({ length: 3 }).map((_, i) => <div key={i} className={`skeleton h-9 w-2/3 rounded-xl ${i % 2 ? 'self-end' : ''}`} />)}
        {lines.map((l) => (
          <div
            key={l.id}
            className={[
              'max-w-[80%] px-3 py-2 text-ui rounded-[14px] whitespace-pre-wrap break-words',
              l.mine ? 'self-end bg-[var(--color-human-soft)] border border-[var(--color-human-line)] rounded-ee-[4px]' : 'self-start bg-[var(--color-surface-2)] border border-[var(--line)] rounded-es-[4px]',
            ].join(' ')}
            dir="auto"
          >
            {l.text}
            <div className="text-caption text-[var(--ink-3)] mt-0.5">{formatTime(l.at)}</div>
          </div>
        ))}
      </div>
      <footer className="p-2 border-t border-[var(--line)] shrink-0">
        {canSend ? (
          <form
            className="flex items-end gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder={t('dock.placeholder')}
              aria-label={t('dock.placeholder')}
              className="input flex-1 min-h-[40px] max-h-28 resize-none py-2"
              dir="auto"
            />
            <button type="submit" className="btn btn-primary w-10 h-10 p-0 shrink-0" disabled={!text.trim() || reply.isPending} aria-label={t('dock.send')}>
              <Icons.send size={16} aria-hidden="true" />
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-2 text-caption text-[var(--ink-2)] px-1 py-1">
            <span className="flex-1">{t('dock.cannotReply')}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenInbox}>
              {t('dock.openInbox')}
            </button>
          </div>
        )}
      </footer>
    </section>
  );
}

/**
 * Messages, docked at the bottom of every page (LinkedIn / Facebook style):
 * a bar with the unread count; opened, the latest conversations; a
 * conversation opens as a small chat window with its message box. "Open in
 * Inbox" goes to the full conversation. Not on the Inbox itself, the
 * composer or setup flows, and only on desktop widths — phones have the
 * Inbox tab.
 */
export function MessagesDock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { role } = useCanWrite();
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const { unread } = useInboxBadges();
  const [open, setOpen] = useState(false);
  const [chatKey, setChatKey] = useState<string | null>(null);
  const hidden = HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`)) || !canReadInbox(role);

  const list = useQuery({
    queryKey: ['inbox', 'dock', wsId],
    queryFn: () => inboxService.listConversations({ view: 'all', limit: DOCK_LIMIT }),
    enabled: !!wsId && (open || chatKey !== null) && !hidden,
  });
  const convs = (list.data?.data ?? []) as Summary[];
  const chat = convs.find((c) => c.key === chatKey) ?? null;
  useEffect(() => {
    if (hidden) return;
    document.documentElement.style.setProperty('--dock-h', '56px');
    return () => {
      document.documentElement.style.removeProperty('--dock-h');
    };
  }, [hidden]);
  if (hidden) return null;

  const openInbox = (key?: string): void => {
    navigate(key ? `${ROUTES.INBOX}?conversation=${encodeURIComponent(key)}` : ROUTES.INBOX);
  };

  return (
    <div className="hidden lg:flex fixed bottom-0 end-6 z-[40] items-end gap-3 pointer-events-none" data-testid="messages-dock">
      {chat && <ChatWindow c={chat} onClose={() => setChatKey(null)} onOpenInbox={() => openInbox(chat.key)} />}
      <section className="pointer-events-auto w-[300px] flex flex-col bg-[var(--color-surface)] border border-b-0 border-[var(--line)] rounded-t-[12px] shadow-[var(--shadow-xl)]" aria-label={t('dock.title')}>
        <div className="h-12 flex items-center gap-2 ps-3 pe-1.5">
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 flex items-center gap-2 bg-transparent border-none cursor-pointer text-start h-10" aria-expanded={open}>
            <Icons.inbox size={18} className="text-[var(--ink-2)]" aria-hidden="true" />
            <span className="text-ui font-semibold flex-1">{t('dock.title')}</span>
            {unread > 0 && (
              <span className="text-caption font-semibold px-[6px] rounded-full leading-[1.5] tabular-nums bg-[var(--color-accent)] text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
          <button type="button" className="btn btn-ghost btn-icon-sm" onClick={() => openInbox()} aria-label={t('dock.openInbox')} title={t('dock.openInbox')}>
            <Icons.externalLink size={16} aria-hidden="true" />
          </button>
          <button type="button" className="btn btn-ghost btn-icon-sm" onClick={() => setOpen((v) => !v)} aria-label={open ? t('dock.collapse') : t('dock.expand')}>
            {open ? <Icons.chevronDown size={16} aria-hidden="true" /> : <Icons.chevronUp size={16} aria-hidden="true" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-[var(--line)] max-h-[420px] overflow-y-auto overscroll-contain">
            {list.isLoading && (
              <div role="status" aria-live="polite" data-testid="dock-loading">
                <p className="m-0 px-3 pt-2.5 text-caption text-[var(--ink-3)]">{t('dock.loading')}</p>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex gap-2.5 px-3 py-2.5" aria-hidden="true">
                    <div className="skeleton w-8 h-8 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <div className="skeleton h-3 w-1/2" />
                      <div className="skeleton h-3 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {list.isError && (
              <div role="alert" className="flex flex-col items-center gap-2 px-4 py-6 text-center" data-testid="dock-error">
                <p className="m-0 text-ui text-[var(--ink-2)]">{t('dock.loadFailed')}</p>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => void list.refetch()} disabled={list.isFetching}>
                  {t('common.retry')}
                </button>
              </div>
            )}
            {list.isSuccess && convs.length === 0 && (
              <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
                <span className="text-2xl" aria-hidden="true">📭</span>
                <p className="m-0 text-ui text-[var(--ink-3)]">{t('dock.empty')}</p>
              </div>
            )}
            {convs.map((c) => {
              const last = c.events[0];
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setChatKey(c.key)}
                  className={['w-full flex items-center gap-2.5 px-3 py-2.5 text-start bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-2)]', chatKey === c.key ? 'bg-[var(--color-accent-soft)]' : ''].join(' ')}
                >
                  <Avatar c={c} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className={`text-ui truncate flex-1 ${c.unread ? 'font-semibold text-[var(--ink)]' : 'text-[var(--ink-2)]'}`} dir="auto">{nameOf(c)}</span>
                      <span className="text-caption text-[var(--ink-3)] shrink-0">{formatRelative(c.lastAt)}</span>
                    </span>
                    <span className="block text-caption text-[var(--ink-3)] truncate" dir="auto">{last?.message ?? ''}</span>
                  </span>
                  {c.unread && <span className="w-2 h-2 rounded-full bg-[var(--color-accent)] shrink-0" aria-label={t('dock.unread')} />}
                </button>
              );
            })}
            {convs.length > 0 && (
              <div className="border-t border-[var(--line)] p-2 text-center">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => openInbox()}>
                  {t('dock.openInbox')}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
